'use client'

import { useEffect, useState, useMemo } from 'react'
import Link from 'next/link'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import { Plus_Jakarta_Sans } from "next/font/google"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { useGetNewsByTypeIdQuery } from '@/hooks/useGetNewsByTypeIdQueryHook/useGetNewsByTypeIdQuery'
import { useGetNewsGroupsQuery } from '@/services/ecoType/queries'
import { useGetNewsByNewTypeIdQuery } from '@/services/newsHome'

// Cấu hình font chữ giao diện
const jakarta = Plus_Jakarta_Sans({
  subsets: ['vietnamese'],
  weight: ['400', '500', '600', '700'],
  display: 'swap'
})

// Hàm gộp className cho Tailwind
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Interface dữ liệu loại tin tức
interface NewsTypeItem {
  id: number
  title: string
  description: string
  image: string
  tag: string
  slug: string
}

// Component hiển thị danh sách tin tức theo danh mục
export default function ListNews() {
  // Cấu hình số lượng item mỗi trang
  const PAGE_SIZE = 12
  // State loại tin hiện tại
  const [currentTypeId, setCurrentTypeId] = useState<number>(0)
  // State trang hiện tại
  const [currentPage, setCurrentPage] = useState(1)

  // Hook lấy query params và điều hướng
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  // Hook lấy danh sách nhóm tin tức
  const { data: apiResponse, isLoading: groupsLoading } = useGetNewsGroupsQuery()

  // Hàm tạo slug từ tiêu đề
  const createSlug = (title: string) => {
    return title.toLowerCase()
      .trim()
      .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
      .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
      .replace(/[iíìỉĩị]/g, 'i')
      .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
      .replace(/[úùủũụưứừửữự]/g, 'u')
      .replace(/[ýỳỷỹỵ]/g, 'y')
      .replace(/đ/g, 'd')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Memo xử lý dữ liệu danh mục tin tức
  const DATAFILTER: NewsTypeItem[] = useMemo(() => {
    const data = apiResponse as any
    if (!data) return []
    const root = data.result || (data.id ? data : null) || data?.data?.result
    if (!root) return []
    const children = root.children || []
    const combinedList = [root, ...children]

    return combinedList.map((item: any) => {
      const title = item.title || item.newsTypeName || '';
      return {
        id: item.id,
        title: title,
        description: item.description || '',
        image: item.image || '',
        tag: item.keywords || '',
        slug: createSlug(title)
      }
    })
      .filter((item) => item.title?.toLowerCase() !== 'nổi bật')
  }, [apiResponse])


  // Hook lấy danh sách tin tức theo loại và phân trang
  const { data: DATANEWS, isLoading: newsLoading } = useGetNewsByNewTypeIdQuery({
    payload: { keywords: '', newsTypeId: currentTypeId, page: currentPage, pageSize: PAGE_SIZE, isPublic: '' },
  })

  // Tính tổng số trang phân trang
  const totalPages = DATANEWS?.result?.pagingInfo?.totalItems
    ? Math.ceil(DATANEWS.result.pagingInfo.totalItems / PAGE_SIZE)
    : 0

  // Effect đồng bộ slug trên URL với ID loại tin
  useEffect(() => {
    if (DATAFILTER && DATAFILTER.length > 0) {

      const foundItem = DATAFILTER.find(item => searchParams.has(item.slug));

      if (foundItem) {
        if (currentTypeId !== foundItem.id) {
          setCurrentTypeId(foundItem.id);
          setCurrentPage(1);
        }
      } else {
        if (DATAFILTER.length > 0 && currentTypeId === 0) {
          setCurrentTypeId(DATAFILTER[0].id);
          setCurrentPage(1);
        }
      }
    }
  }, [DATAFILTER, searchParams, currentTypeId])

  // Hàm xử lý chọn loại tin và cập nhật URL
  const handleSelectType = (item: NewsTypeItem) => {
    if (currentTypeId === item.id) return;

    setCurrentTypeId(item.id)
    setCurrentPage(1)

    router.push(`${pathname}?${item.slug}`, { scroll: false })
  }

  // Hàm format ngày hiển thị
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return ''
    return new Date(timestamp * 1000).toLocaleDateString('vi-VN')
  }

  // Hàm chuyển trang phân trang
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div>
      {/* Khối hiển thị danh sách danh mục tin */}
      <section className="max-w-6xl mx-auto px-4 mt-8">
        {groupsLoading && <div className="text-center text-gray-400 py-2">Đang tải danh mục...</div>}

        <div className="flex flex-wrap gap-2 justify-center">
          {DATAFILTER.map((group) => (
            <button
              key={group.id}
              onClick={() => handleSelectType(group)}
              className={`px-5 py-1.5 rounded-sm border text-sm text-[#252C32] transition
                ${currentTypeId === group.id
                  ? 'bg-[#006633] text-white border-[#767676]'
                  : 'bg-white hover:bg-gray-100 text-gray-700'
                }`}
            >
              {group.title}
            </button>
          ))}
        </div>
      </section>

      {/* Khối hiển thị danh sách bài viết */}
      <section className="px-4 md:px-16 lg:px-32 mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {newsLoading && <p>Loading news...</p>}

          {DATANEWS?.result?.items?.map((item) => {
            const slugTitle = createSlug(item.title || '');
            const postHref = `/news/${slugTitle}-${item.id}`;

            return (
              <Link key={item.id} href={postHref} className="block h-full hover:no-underline">
                <div className={cn("group flex flex-col gap-3 2xl:gap-4 min-[1920px]:gap-6 overflow-hidden h-full")}>
                  <div className="relative aspect-[16/10] overflow-hidden rounded-lg bg-gray-50">
                    <img
                      src={item.image || 'https://placehold.co/600x400?text=No+Image'}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 rounded-full bg-[#005a32] px-3 py-1 min-[1920px]:px-4 min-[1920px]:py-2 text-[10px] min-[1920px]:text-base font-medium text-white shadow-sm">
                      {item.newsType?.newsTypeName || "Tin tức"}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 min-[1920px]:gap-4 flex-1">
                    <div className="text-xs 2xl:text-sm min-[1920px]:text-lg text-[#9F9F9F] font-normal">
                      Ngày đăng • <span className="text-[#2D3746]">{formatDate(item.updatedDateTS)}</span>
                    </div>

                    <h3 className={cn(
                      jakarta.className,
                      "line-clamp-2 text-base 2xl:text-lg min-[1920px]:text-2xl font-bold leading-snug text-[#2D3746] transition-colors duration-300 group-hover:text-[#005a32]"
                    )}>
                      {item.title}
                    </h3>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </section>

      {/* Khối phân trang */}
      {totalPages > 1 && (
        <section className="mt-12 mb-16">
          <div className="flex justify-center items-center gap-1.5">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="group flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 bg-white text-black transition-all hover:bg-gray-100 hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed disabled:border-gray-200"
            >
              <ChevronLeft className="h-2.5 w-2.5" strokeWidth={2} />
            </button>

            <div className="flex items-center gap-0.5">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-bold transition-all
                    ${page === currentPage
                      ? 'bg-[#006633] text-white'
                      : 'bg-transparent text-gray-500 hover:text-[#006633]'
                    }`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="group flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 bg-white text-black transition-all hover:bg-gray-100 hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed disabled:border-gray-200"
            >
              <ChevronRight className="h-2.5 w-2.5" strokeWidth={2} />
            </button>
          </div>
        </section>
      )}
    </div>
  )
}